// Thumbnail generation service — standardized interface for requesting,
// copying, and scheduling thumbnail generation across the studio.
//
// Two producers, picked by what the project actually is:
//
//   draft (every project until it is published)
//     → rasterise the studio's OWN render of the project (the IDE build-mode
//       canvas) and keep the bytes on this origin. The platform's Browserless
//       producer cannot serve drafts: it screenshots `https://{slug}.cogo.xyz`,
//       and a draft has no live subdomain (measured 2026-07-31 — both sampled
//       draft slugs answered 404 "App Not Found"), so it burned a request and
//       returned nothing every single time.
//
//   published
//     → the existing platform screenshot + R2 upload, which has a real page to
//       shoot.
//
// Thumbnail generation is expensive, so all methods are non-blocking and
// debounced where appropriate.

import { getStudioAccessToken } from '@/services/auth-provider'
import {
  getCogoPlatformBase,
  withCogoDevProxyHeaderRecord,
} from '@/services/cogo-platform-config'
import { captureThumbnailFromElement } from '@/features/project/lib/project-thumbnail-capture'
import { putProjectThumbnail } from '@/features/project/lib/project-thumbnail-store'

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const REFRESH_DEBOUNCE_MS = 30_000 // 30 seconds — avoid hammering after edits

function getCfBaseUrl(): string {
  return (import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env?.VITE_COGO_CLOUDFLARE_URL
    ?? (typeof process !== 'undefined' ? process.env.VITE_COGO_CLOUDFLARE_URL : undefined)
    ?? ''
}

function getProjectApiBaseUrl(): string {
  return getCogoPlatformBase()
}

async function getUserJwt(): Promise<string | null> {
  return getStudioAccessToken()
}

/**
 * Whether the thumbnail HTTP API is reachable from this environment.
 *
 * The `/api/project/thumbnail` endpoint lives on the Cloudflare Pages
 * Function deployment (cfBase). When running `vite --port 3003` directly
 * for local development the endpoint is absent, so any call would 404 and
 * pollute the console with errors that look like bugs (F-002 from the
 * 2026-05-03 E2E walk). Callers should short-circuit on `false`.
 */
function isThumbnailApiAvailable(): boolean {
  return Boolean(getCfBaseUrl())
}

// ---------------------------------------------------------------------------
// Capture sources — registered by whatever surface is currently rendering a
// project (today: the IDE build-mode canvas).
// ---------------------------------------------------------------------------

type CaptureSource = () => HTMLElement | null

const captureSources = new Map<string, CaptureSource>()

/**
 * Register the DOM node that currently holds a live render of `projectId`.
 * Returns an unregister function; re-registering the same project replaces the
 * previous source.
 */
export function registerProjectThumbnailCaptureSource(
  projectId: string,
  getElement: CaptureSource,
): () => void {
  captureSources.set(projectId, getElement)
  return () => {
    if (captureSources.get(projectId) === getElement) captureSources.delete(projectId)
  }
}

export function hasProjectThumbnailCaptureSource(projectId: string): boolean {
  return captureSources.has(projectId)
}

/**
 * Capture from the registered render surface and persist to this origin.
 * Returns false when there is nothing to capture or the render is blank —
 * in both cases the card keeps its honest placeholder.
 */
export async function captureProjectThumbnailNow(projectId: string): Promise<boolean> {
  const source = captureSources.get(projectId)
  if (!source) return false
  const result = await captureThumbnailFromElement(source())
  if (!result.ok) return false
  return putProjectThumbnail({
    projectId,
    blob: result.capture.blob,
    width: result.capture.width,
    height: result.capture.height,
  })
}

// ---------------------------------------------------------------------------
// Internal state for debounced refresh
// ---------------------------------------------------------------------------

interface PendingRefresh {
  projectId: string
  slug: string
  isPublished: boolean
}

let pendingRefresh: PendingRefresh | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null

// ---------------------------------------------------------------------------
// ThumbnailService
// ---------------------------------------------------------------------------

export const ThumbnailService = {
  registerCaptureSource: registerProjectThumbnailCaptureSource,
  captureNow: captureProjectThumbnailNow,

  /**
   * Request thumbnail generation for a PUBLISHED project via the platform API.
   * Non-blocking — fires request and returns the thumbnail URL (or null on failure).
   * The API generates a screenshot via Browserless.io, uploads to R2,
   * and returns the public URL. We then persist it to the DB.
   *
   * Never call this for a draft: the platform shoots `https://{slug}.cogo.xyz`,
   * which does not exist until the project is published.
   */
  async requestGeneration(projectId: string, slug: string): Promise<string | null> {
    if (!isThumbnailApiAvailable()) {
      // Dev mode without VITE_COGO_CLOUDFLARE_URL — no API to call.
      return null
    }
    try {
      const cfBase = getCfBaseUrl()
      const url = `${cfBase}/api/project/thumbnail`

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, projectId }),
      })

      if (!res.ok) {
        console.warn('[ThumbnailService] generation request failed:', res.status)
        return null
      }

      const data = await res.json() as { thumbnailUrl?: string }
      const thumbnailUrl = data.thumbnailUrl ?? null

      // Persist to DB if we got a URL back
      if (thumbnailUrl) {
        const jwt = await getUserJwt()
        if (jwt) {
          const persistRes = await fetch(`${getProjectApiBaseUrl()}/api/projects/${encodeURIComponent(projectId)}`, {
            method: 'PATCH',
            headers: withCogoDevProxyHeaderRecord({
              'Content-Type': 'application/json',
              Authorization: `Bearer ${jwt}`,
            }),
            body: JSON.stringify({ thumbnail: thumbnailUrl }),
          })
          if (!persistRes.ok) {
            console.warn('[ThumbnailService] thumbnail persist failed:', persistRes.status)
          }
        }
      }

      return thumbnailUrl
    } catch (err) {
      console.warn('[ThumbnailService] requestGeneration error:', err)
      return null
    }
  },

  /**
   * Copy thumbnail from a template to a newly created project.
   * Reuses the template's existing thumbnail — avoids an expensive
   * Browserless call for projects created from templates.
   *
   * The path probed here is the studio's own `/thumbnails/{templateId}.png`,
   * the same one `template-loader.ts` hands to the template gallery. The
   * previous implementation probed `{cfBase}/thumbnails/templates/{id}.png`,
   * a route the platform worker does not serve (it exposes `/artifacts/:slug/:file`
   * and `/assets/:path`), so the HEAD was a guaranteed 404 and this function
   * had never returned a URL. Staying on the studio origin also keeps the
   * result loadable under COEP.
   */
  async copyFromTemplate(templateId: string, _projectSlug: string): Promise<string | null> {
    if (typeof fetch !== 'function' || typeof window === 'undefined') return null
    try {
      const thumbnailPath = `/thumbnails/${encodeURIComponent(templateId)}.png`

      // Verify the thumbnail exists before persisting
      const checkRes = await fetch(thumbnailPath, { method: 'HEAD' })
      if (!checkRes.ok) {
        console.warn('[ThumbnailService] template thumbnail not found:', templateId)
        return null
      }

      return thumbnailPath
    } catch (err) {
      console.warn('[ThumbnailService] copyFromTemplate error:', err)
      return null
    }
  },

  /**
   * Schedule a deferred thumbnail refresh (debounced).
   * Used during auto-save — doesn't fire immediately, waits REFRESH_DEBOUNCE_MS.
   * Subsequent calls reset the timer so only the last one fires.
   */
  scheduleRefresh(
    projectId: string,
    slug: string,
    opts: { isPublished?: boolean } = {},
  ): void {
    pendingRefresh = { projectId, slug, isPublished: opts.isPublished === true }

    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      const pending = pendingRefresh
      pendingRefresh = null
      if (pending) {
        // Fire-and-forget — don't block the caller
        void runRefresh(pending).catch(() => {
          // Silently swallow — thumbnail refresh is best-effort
        })
      }
    }, REFRESH_DEBOUNCE_MS)
  },

  /**
   * Flush any pending thumbnail generation immediately.
   * Call on logout / page unload to ensure the request fires.
   * Uses navigator.sendBeacon() for the published path, which is more
   * reliable during unload than fetch.
   */
  flush(): void {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }

    const pending = pendingRefresh
    pendingRefresh = null

    if (!pending) return

    // Draft: capture locally. Rasterising cannot complete during unload, but a
    // flush also happens on logout / project close where it can.
    if (!pending.isPublished) {
      void captureProjectThumbnailNow(pending.projectId).catch(() => {})
      return
    }

    if (!isThumbnailApiAvailable()) return

    const cfBase = getCfBaseUrl()
    const url = `${cfBase}/api/project/thumbnail`

    const payload = JSON.stringify({
      slug: pending.slug,
      projectId: pending.projectId,
    })

    // sendBeacon is more reliable during page unload than fetch
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
    } else {
      // Fallback: fire-and-forget fetch (may not complete during unload)
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {})
    }
  },
}

/**
 * Producer selection. A local capture is preferred even for published projects
 * when a render surface is registered: it is free, immediate, and shows the
 * version currently being edited rather than the last deployed one.
 */
async function runRefresh(pending: PendingRefresh): Promise<void> {
  const captured = await captureProjectThumbnailNow(pending.projectId)
  if (captured) return
  if (!pending.isPublished) return
  await ThumbnailService.requestGeneration(pending.projectId, pending.slug)
}
